/**
 * ヘッダーコンポーネント
 *
 * 機能一覧:
 * - ハンバーガーメニューの開閉制御
 * - スクロールに応じたヘッダーの表示/非表示切り替え
 * - コンパクトヘッダーへの自動切り替え
 * - プルダウンメニューのアニメーション制御（PC/SP対応）
 * - レスポンシブ対応（PC/SP/タブレット）
 * - スムーズなアニメーション処理
 */
export default class Header {
    constructor() {
        // =================================
        // DOM要素の取得
        // =================================
        this.header = document.getElementById('js-header');
        this.headerTrigger = document.getElementById('js-header-trigger'); // ハンバーガーボタン
        this.headerNav = document.getElementById('js-header-nav'); // ナビゲーション
        this.headerContent = document.getElementById('js-header-content'); // ヘッダーコンテンツ
        this.html = document.documentElement;
        this.body = document.body;

        // プルダウンメニュー関連の要素
        this.gnavItems = document.querySelectorAll('.js-gnav'); // グローバルナビアイテム
        this.gnavTriggers = document.querySelectorAll('.js-gnav-trigger'); // プルダウントリガー
        this.gnavContents = document.querySelectorAll('.js-gnav-content'); // プルダウンコンテンツ
        this.gnavTriggersSub = document.querySelectorAll('.js-gnav-trigger-sub'); // プルダウントリガー(SP用)
        this.gnavContentsSub = document.querySelectorAll('.js-gnav-content-sub'); // プルダウンコンテンツ(SP用)

        // =================================
        // 状態管理変数
        // =================================
        this.isMenuOpen = false; // ハンバーガーメニューの開閉状態
        this.lastScrollY = 0; // 前回のスクロール位置
        this.headerHeight = 0; // ヘッダーの高さ
        this.isScrollingDown = false; // スクロール方向
        this.throttleTimer = null; // スロットル制御用

        // =================================
        // 設定値
        // =================================
        this.breakpoint = 1000; // PC/SPの切り替えブレイクポイント
        this.isDesktop = window.innerWidth > this.breakpoint;

        // =================================
        // アニメーション管理
        // =================================
        this.activeAnimations = new Map(); // アクティブなアニメーションの管理
        this.hoverTimeouts = new Map(); // ホバー遅延タイマーの管理

        // 初期化処理
        this.init();
    }

    /**
     * 初期化処理
     * ヘッダー要素の存在確認と各種設定を行う
     */
    init() {
        // ヘッダー要素の存在確認
        if (!this.header) {
            return;
        }

        this.setInitialValues();
        this.setEventListeners();
    }

    /**
     * 初期値設定
     * スクロール位置、ヘッダー高さ、初期状態の設定
     */
    setInitialValues() {
        this.lastScrollY = window.scrollY;
        this.headerHeight = this.header.offsetHeight;
        this.resetMenuStates();

        // PC版の場合、初期スクロール位置に基づいてコンパクトヘッダーを設定
        if (window.innerWidth > this.breakpoint && this.lastScrollY > this.headerHeight) {
            this.header.classList.add('is-compact');
            this.header.classList.remove('is-init');
        }
    }

    /**
     * イベントリスナー設定
     * 各種ユーザーインタラクションのイベントを設定
     */
    setEventListeners() {
        // ハンバーガーメニューのクリックイベント
        if (this.headerTrigger) {
            this.headerTrigger.addEventListener('click', this.toggleHamburgerMenu.bind(this));
        }

        // スクロールイベント（パフォーマンス最適化のためスロットル適用）
        window.addEventListener('scroll', this.throttle(this.handleScroll.bind(this), 16));

        // リサイズイベント（パフォーマンス最適化のためスロットル適用）
        window.addEventListener('resize', this.throttle(this.handleResize.bind(this), 100));

        // プルダウンメニューのイベント設定
        this.setupDropdownEvents();
    }

    // =================================
    // プルダウンメニュー制御
    // =================================

    /**
     * プルダウンメニューのイベント設定
     * PC版（ホバー）とSP版（クリック）で異なる動作を設定
     */
    setupDropdownEvents() {
        this.gnavTriggers.forEach((trigger, index) => {
            const content = this.gnavContents[index];
            if (!content) return;

            if (!content.id) content.id = `js-gnav-content-${index + 1}`;
            trigger.setAttribute('aria-controls', content.id);
            trigger.setAttribute('aria-expanded', 'false');

            // SP版のクリックイベント
            trigger.addEventListener('click', (e) => {
                if (window.innerWidth <= this.breakpoint) {
                    e.preventDefault();
                    // タッチ操作最適化のため少し遅延
                    setTimeout(() => {
                        this.toggleDropdown(content);
                    }, 50);
                }
            });

            // PC版のホバーイベント。実行時に画面幅を判定することで、
            // リサイズ後もリスナーを再登録せず同じハンドラーを利用する。
            const parentItem = trigger.closest('.js-gnav');
            if (parentItem) {
                parentItem.addEventListener('mouseenter', () => {
                    if (window.innerWidth > this.breakpoint) {
                        this.showDropdown(content);
                    }
                });

                parentItem.addEventListener('mouseleave', () => {
                    if (window.innerWidth > this.breakpoint) {
                        this.hideDropdown(content);
                    }
                });
            }
        });

        // SP版のみのサブプルダウンメニューのイベント設定
        this.gnavTriggersSub.forEach((trigger, index) => {
            const content = this.gnavContentsSub[index];
            if (!content) return;

            if (!content.id) content.id = `js-gnav-sub-content-${index + 1}`;
            trigger.setAttribute('aria-controls', content.id);
            trigger.setAttribute('aria-expanded', 'false');

            trigger.addEventListener('click', (e) => {
                if (window.innerWidth > this.breakpoint) return;

                e.preventDefault();
                e.stopPropagation(); // イベントバブリングを停止

                // is-showクラスとis-hideクラスをトグルする
                trigger.classList.toggle('is-active');

                setTimeout(() => {
                    this.toggleSubDropdown(content);
                }, 50);
            });
        });
    }

    /**
     * プルダウンメニューの表示/非表示切り替え
     * @param {HTMLElement} content - プルダウンコンテンツ要素
     */
    toggleDropdown(content) {
        const isVisible = content.classList.contains('is-show');
        const gnavItem = this.findGnavItemByContent(content);

        if (window.innerWidth <= this.breakpoint && gnavItem && gnavItem.classList.contains('is-clinic')) {
            this.openClinicDropdownSP();
            return;
        }

        if (isVisible) {
            this.hideDropdown(content);
        } else {
            this.showDropdown(content);
        }
    }

    /**
     * サブプルダウンメニューの表示/非表示切り替え（親要素を閉じない）
     * @param {HTMLElement} content - サブプルダウンコンテンツ要素
     */
    toggleSubDropdown(content) {
        const isVisible = content.classList.contains('is-show');

        if (isVisible) {
            this.hideDropdown(content);
        } else {
            this.showSubDropdown(content);
        }
    }

    /**
     * プルダウンメニューの表示
     * デバイスタイプに応じて適切なアニメーションを選択
     * @param {HTMLElement} content - プルダウンコンテンツ要素
     */
    showDropdown(content) {
        this.setDropdownExpanded(content, true);
        if (window.innerWidth > this.breakpoint) {
            this.showDropdownPC(content); // PC版アニメーション
        } else {
            this.showDropdownSP(content); // SP版アニメーション
        }
    }

    /**
     * サブプルダウンメニューの表示（親要素を閉じない）
     * @param {HTMLElement} content - サブプルダウンコンテンツ要素
     */
    showSubDropdown(content) {
        this.setDropdownExpanded(content, true);
        if (window.innerWidth > this.breakpoint) {
            this.showDropdownPC(content); // PC版アニメーション
        } else {
            this.showSubDropdownSP(content); // SP版サブアニメーション
        }
    }

    /**
     * プルダウンメニューの非表示
     * デバイスタイプに応じて適切なアニメーションを選択
     * @param {HTMLElement} content - プルダウンコンテンツ要素
     */
    hideDropdown(content) {
        this.setDropdownExpanded(content, false);
        if (window.innerWidth > this.breakpoint) {
            this.hideDropdownPC(content); // PC版アニメーション
        } else {
            this.hideDropdownSP(content); // SP版アニメーション
        }
    }

    /**
     * 全てのプルダウンメニューを非表示
     * メニュー切り替え時やリサイズ時に使用
     */
    hideAllDropdowns() {
        this.gnavContents.forEach(content => {
            if (content.classList.contains('is-show')) {
                this.hideDropdown(content);
            }
        });

        this.gnavItems.forEach(item => {
            item.classList.remove('is-active');
        });
    }

    /**
     * 指定されたプルダウン以外を非表示
     * SPメニュー内の切り替え時にメニュー全体の状態を初期化しないために使用
     * @param {HTMLElement} currentContent - 現在操作中のプルダウンコンテンツ要素
     */
    hideOtherDropdowns(currentContent) {
        this.gnavContents.forEach(content => {
            if (content !== currentContent && content.classList.contains('is-show')) {
                this.hideDropdown(content);
            }
        });
    }

    /**
     * プルダウンコンテンツに対応するgnavItemを取得
     * @param {HTMLElement} content - プルダウンコンテンツ要素
     * @returns {HTMLElement|null} - 対応するgnavItem要素
     */
    findGnavItemByContent(content) {
        for (let i = 0; i < this.gnavContents.length; i++) {
            if (this.gnavContents[i] === content) {
                return this.gnavItems[i];
            }
        }
        return null;
    }

    setDropdownExpanded(content, expanded) {
        const mainIndex = Array.from(this.gnavContents).indexOf(content);
        const subIndex = Array.from(this.gnavContentsSub).indexOf(content);
        const trigger = mainIndex >= 0
            ? this.gnavTriggers[mainIndex]
            : this.gnavTriggersSub[subIndex];

        if (trigger) {
            trigger.setAttribute('aria-expanded', String(expanded));
        }
    }

    /**
     * サブプルダウンの親プルダウンを特定
     * @param {HTMLElement} subContent - サブプルダウンコンテンツ要素
     * @returns {HTMLElement|null} - 親プルダウンコンテンツ要素
     */
    findParentDropdown(subContent) {
        // サブプルダウンの親要素をたどって親プルダウンを見つける
        let parentElement = subContent.parentElement;

        while (parentElement && !parentElement.classList.contains('js-gnav-content')) {
            parentElement = parentElement.parentElement;
        }

        return parentElement;
    }

    /**
     * 指定されたサブプルダウン以外の他のサブプルダウンを閉じる（親要素は維持）
     * @param {HTMLElement} currentSubContent - 現在のサブプルダウンコンテンツ要素
     * @param {HTMLElement} parentContent - 親プルダウンコンテンツ要素
     */
    hideOtherSubDropdowns(currentSubContent, parentContent) {
        // 全てのサブプルダウンをチェック
        this.gnavContentsSub.forEach(subContent => {
            if (subContent !== currentSubContent && subContent.classList.contains('is-show')) {
                // 同じ親の下にあるサブプルダウンかチェック
                const thisParent = this.findParentDropdown(subContent);
                if (thisParent === parentContent) {
                    // 同じ親の下の他のサブプルダウンを閉じる
                    this.hideDropdown(subContent);
                }
            }
        });

        // メインプルダウンもチェック（現在開いている親以外）
        this.gnavContents.forEach(content => {
            if (content !== parentContent && content.classList.contains('is-show')) {
                this.hideDropdown(content);
            }
        });
    }

    // =================================
    // ハンバーガーメニュー制御
    // =================================

    /**
     * ハンバーガーメニューの開閉切り替え
     * SP版でのみ動作
     */
    toggleHamburgerMenu() {
        if (window.innerWidth <= this.breakpoint) {
            this.isMenuOpen = !this.isMenuOpen;

            // 初回表示時の初期化クラスを解除
            if (this.headerTrigger.classList.contains('is-init')) {
                this.headerTrigger.classList.remove('is-init');
            }

            // メニューの表示/非表示を切り替え
            if (this.isMenuOpen) {
                this.showHamburgerMenu();
            } else {
                this.hideHamburgerMenu();
            }
        }
    }

    /**
     * ハンバーガーメニューの表示
     * ナビゲーション表示とbodyスクロール固定
     */
    showHamburgerMenu() {
        // 初期化クラスを削除
        this.headerNav.classList.remove('is-init');

        // ナビゲーション表示アニメーション
        this.headerNav.classList.remove('is-hide');
        this.headerNav.classList.add('is-show');

        // ハンバーガーボタンをアクティブ状態に
        this.headerTrigger.classList.add('is-active');
        this.headerTrigger.setAttribute('aria-expanded', 'true');
        this.headerTrigger.setAttribute('aria-label', 'メニューを閉じる');

        // bodyスクロールを固定
        this.lockBody();

        this.openClinicDropdownSP();

        this.isMenuOpen = true;
    }

    /**
     * SP版でクリニック一覧を常時開いた状態にする
     */
    openClinicDropdownSP() {
        if (window.innerWidth > this.breakpoint || !this.headerNav) return;

        const clinicItem = this.headerNav.querySelector('.l-header-nav__item.is-clinic');
        const content = clinicItem ? clinicItem.querySelector('.js-gnav-content') : null;
        if (!clinicItem || !content) return;

        this.cancelAnimation(content);

        clinicItem.classList.add('is-active');
        this.setDropdownExpanded(content, true);
        content.classList.add('is-show');
        content.style.display = 'flex';
        content.style.visibility = 'visible';
        content.style.maxHeight = 'none';
        content.style.opacity = '1';
    }

    /**
     * ハンバーガーメニューの非表示
     * ナビゲーション非表示とbodyスクロール固定解除
     */
    hideHamburgerMenu() {
        // ナビゲーション非表示アニメーション
        this.headerNav.classList.remove('is-show');
        this.headerNav.classList.add('is-hide');

        // ハンバーガーボタンの非アクティブ化
        this.headerTrigger.classList.remove('is-active');
        this.headerTrigger.setAttribute('aria-expanded', 'false');
        this.headerTrigger.setAttribute('aria-label', 'メニューを開く');

        // bodyスクロール固定を解除
        this.unlockBody();

        this.isMenuOpen = false;
    }

    // =================================
    // スクロール制御
    // =================================

    /**
     * スクロールイベントハンドラー
     * ヘッダーの表示/非表示とコンパクト表示を制御
     */
    handleScroll() {
        const currentScrollY = window.scrollY;
        const scrollDifference = currentScrollY - this.lastScrollY;

        // PC版でのコンパクトヘッダー制御
        if (window.innerWidth > this.breakpoint) {
            if (currentScrollY > this.headerHeight) {
                // コンパクト化時の初期化クラス削除
                setTimeout(() => {
                    this.header.classList.add('is-compact');
                }, 10);
            } else {
                this.header.classList.remove('is-compact');
            }
        }

        // スクロール方向に応じたヘッダー表示制御
        if (scrollDifference > 0 && currentScrollY > this.headerHeight) {
            // 下方向スクロール - ヘッダーを隠す
            this.hideHeader();
        } else if (scrollDifference < 0) {
            // 上方向スクロール - ヘッダーを表示
            this.showHeader();
        }

        this.lastScrollY = currentScrollY;
    }

    /**
     * ヘッダーの非表示
     * 下方向スクロール時に実行
     */
    hideHeader() {
        // 初期化クラスがある場合はアニメーションをスキップ
        if (this.header.classList.contains('is-init')) {
            this.header.classList.remove('is-init');
            return;
        }

        this.header.classList.remove('is-show');
        this.header.classList.add('is-hide');
    }

    /**
     * ヘッダーの表示
     * 上方向スクロール時に実行
     */
    showHeader() {
        // 初期化クラスがある場合はアニメーションをスキップ
        if (this.header.classList.contains('is-init')) {
            this.header.classList.remove('is-init');
            return;
        }

        this.header.classList.remove('is-hide');
        this.header.classList.add('is-show');
    }

    // =================================
    // リサイズ制御
    // =================================

    /**
     * リサイズイベントハンドラー
     * ブレイクポイント切り替え時の状態リセット
     */
    handleResize() {
        const isDesktop = window.innerWidth > this.breakpoint;

        // PC/SPの表示切り替え時だけメニュー状態をリセットする
        if (isDesktop !== this.isDesktop) {
            this.resetMenuStates();
            this.isDesktop = isDesktop;
        }

        this.headerHeight = this.header.offsetHeight;

        // スクロール位置に基づくコンパクトヘッダーの再設定
        const currentScrollY = window.scrollY;
        if (window.innerWidth > this.breakpoint && currentScrollY > this.headerHeight) {
            this.header.classList.add('is-compact');
        }

    }

    /**
     * メニュー状態のリセット
     * リサイズ時やエラー時に全ての状態をクリア
     */
    resetMenuStates() {
        // ハンバーガーメニューを閉じる
        if (this.isMenuOpen) {
            this.hideHamburgerMenu();
        }

        // プルダウンメニューを全て閉じる
        this.hideAllDropdowns();

        // アニメーションをクリーンアップ
        this.cleanupAllAnimations();

        // ヘッダーの状態をリセット
        this.header.classList.remove('is-hide', 'is-show', 'is-compact');

        // bodyの固定を解除
        this.unlockBody();
    }

    // =================================
    // Body固定制御（ハンバーガーメニュー時）
    // =================================

    /**
     * bodyのスクロールを固定
     * ハンバーガーメニュー表示時に実行
     */
    lockBody() {
        // ページの位置を変えず、背景のスクロールだけ停止する
        this.html.classList.add('menu-open');
        this.body.classList.add('menu-open');
    }

    /**
     * bodyのスクロール固定を解除
     * ハンバーガーメニュー非表示時に実行
     */
    unlockBody() {
        // スクロール位置を変更せず、固定だけ解除する
        this.html.classList.remove('menu-open');
        this.body.classList.remove('menu-open');
    }

    // =================================
    // ユーティリティ関数
    // =================================

    /**
     * スロットル関数
     * パフォーマンス最適化のためイベント実行頻度を制御
     * @param {Function} func - 実行する関数
     * @param {number} limit - 実行間隔（ミリ秒）
     * @returns {Function} - スロットル適用済み関数
     */
    throttle(func, limit) {
        let inThrottle;
        return function() {
            const args = arguments;
            const context = this;
            if (!inThrottle) {
                func.apply(context, args);
                inThrottle = true;
                setTimeout(() => inThrottle = false, limit);
            }
        }
    }

    // =================================
    // アニメーション管理
    // =================================

    /**
     * 全てのアクティブなアニメーションをクリーンアップ
     * メモリリーク防止とパフォーマンス最適化
     */
    cleanupAllAnimations() {
        // すべてのアクティブなアニメーションをキャンセル
        this.activeAnimations.forEach((animationId, content) => {
            cancelAnimationFrame(animationId);
            // プルダウンを完全に非表示にする
            content.style.display = 'none';
            content.style.visibility = 'hidden';
            content.style.maxHeight = '0px'; // SP版でも非表示時は0に戻す
            content.style.opacity = '0';
            content.style.transform = 'translateY(-10px)';
            content.classList.remove('is-show', 'is-hide');
        });
        this.activeAnimations.clear();

        // すべてのホバータイムアウトをクリア
        this.hoverTimeouts.forEach((timeoutId) => {
            clearTimeout(timeoutId);
        });
        this.hoverTimeouts.clear();
    }

    /**
     * 指定されたコンテンツのアニメーションをキャンセル
     * @param {HTMLElement} content - プルダウンコンテンツ要素
     */
    cancelAnimation(content) {
        const animationId = this.activeAnimations.get(content);
        if (animationId) {
            cancelAnimationFrame(animationId);
            this.activeAnimations.delete(content);
        }
    }

    /**
     * ホバー遅延タイマーをクリア
     * @param {HTMLElement} content - プルダウンコンテンツ要素
     */
    clearHoverTimeout(content) {
        const timeoutId = this.hoverTimeouts.get(content);
        if (timeoutId) {
            clearTimeout(timeoutId);
            this.hoverTimeouts.delete(content);
        }
    }

    // =================================
    // PC版プルダウンアニメーション
    // =================================

    /**
     * PC版プルダウンメニューの表示アニメーション
     * @param {HTMLElement} content - プルダウンコンテンツ要素
     */
    showDropdownPC(content) {
        // 他のプルダウンを閉じる
        this.hideAllDropdowns();

        // 既に進行中のアニメーションをキャンセル
        this.cancelAnimation(content);

        // ホバー遅延をクリア
        this.clearHoverTimeout(content);

        // 実際の高さを測定
        const targetHeight = this.measureDropdownHeight(content);

        // 初期状態をセット
        content.style.display = 'block';
        content.style.visibility = 'visible';
        content.style.maxHeight = '0px';
        content.style.opacity = '0';
        content.style.overflow = 'hidden';

        // アニメーション開始
        this.animateDropdownShow(content, targetHeight);

    }

    /**
     * PC版プルダウンメニューの非表示アニメーション
     * @param {HTMLElement} content - プルダウンコンテンツ要素
     */
    hideDropdownPC(content) {
        // 既に進行中のアニメーションをキャンセル
        this.cancelAnimation(content);

        // ホバー遅延を設定（誤操作防止）
        const timeoutId = setTimeout(() => {
            this.animateDropdownHide(content);
        }, 50); // 50ms遅延

        this.hoverTimeouts.set(content, timeoutId);
    }

    /**
     * プルダウンコンテンツの高さを測定
     * @param {HTMLElement} content - プルダウンコンテンツ要素
     * @returns {number} - 実際の高さ（px）
     */
    measureDropdownHeight(content) {
        // 元のスタイルを保存
        const originalStyles = {
            display: content.style.display,
            visibility: content.style.visibility,
            maxHeight: content.style.maxHeight,
            position: content.style.position,
            top: content.style.top,
            left: content.style.left
        };

        // 測定用の一時的なスタイル（画面外で表示）
        content.style.display = 'block';
        content.style.visibility = 'hidden';
        content.style.maxHeight = 'none';
        content.style.position = 'absolute';
        content.style.top = '-9999px';
        content.style.left = '-9999px';

        // 実際の高さを取得
        const height = content.offsetHeight;

        // スタイルを元に戻す
        Object.assign(content.style, originalStyles);

        return height;
    }

    /**
     * プルダウン表示アニメーション実行
     * @param {HTMLElement} content - プルダウンコンテンツ要素
     * @param {number} targetHeight - 目標の高さ
     */
    animateDropdownShow(content, targetHeight) {
        const duration = 600; // アニメーション時間（ms）
        const startTime = performance.now();

        const animate = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);

            // イージング関数（ease-out）
            const easeOut = 1 - Math.pow(1 - progress, 3);

            // プロパティの計算
            const currentHeight = targetHeight * easeOut;
            const currentOpacity = easeOut;

            // スタイルの適用
            content.style.maxHeight = `${currentHeight}px`;
            content.style.opacity = currentOpacity;

            if (progress < 1) {
                const animationId = requestAnimationFrame(animate);
                this.activeAnimations.set(content, animationId);
            } else {
                // アニメーション完了
                content.style.maxHeight = `${targetHeight}px`;
                content.style.opacity = '1';
                content.classList.add('is-show');
                this.activeAnimations.delete(content);
            }
        };

        const animationId = requestAnimationFrame(animate);
        this.activeAnimations.set(content, animationId);
    }

    /**
     * プルダウン非表示アニメーション実行
     * @param {HTMLElement} content - プルダウンコンテンツ要素
     */
    animateDropdownHide(content) {
        const duration = 600; // 非表示は少し早く
        const startTime = performance.now();
        const startHeight = content.offsetHeight;

        const animate = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);

            // イージング関数（ease-out）
            const easeOut = 1 - Math.pow(1 - progress, 3);

            // プロパティの計算
            const currentHeight = startHeight * (1 - easeOut);
            const currentOpacity = 1 - easeOut;

            // スタイルの適用
            content.style.maxHeight = `${currentHeight}px`;
            content.style.opacity = currentOpacity;

            if (progress < 1) {
                const animationId = requestAnimationFrame(animate);
                this.activeAnimations.set(content, animationId);
            } else {
                // アニメーション完了
                this.completeHideAnimation(content);
            }
        };

        const animationId = requestAnimationFrame(animate);
        this.activeAnimations.set(content, animationId);
    }

    /**
     * 非表示アニメーション完了処理
     * @param {HTMLElement} content - プルダウンコンテンツ要素
     */
    completeHideAnimation(content) {
        content.style.display = 'none';
        content.style.visibility = 'hidden';
        content.style.maxHeight = '0px';
        content.style.opacity = '0';
        content.classList.remove('is-show');
        this.activeAnimations.delete(content);
    }

    // =================================
    // SP版プルダウンアニメーション
    // =================================

    /**
     * SP版プルダウンメニューの表示アニメーション
     * @param {HTMLElement} content - プルダウンコンテンツ要素
     */
    showDropdownSP(content) {
        // 既に進行中のアニメーションをキャンセル
        this.cancelAnimation(content);

        // 対応するgnavItemにis-activeクラスを追加（SP・タブレットのみ）
        if (window.innerWidth <= this.breakpoint) {
            const gnavItem = this.findGnavItemByContent(content);
            if (gnavItem) {
                gnavItem.classList.add('is-active');
            }
        }

        // 実際の高さを測定
        const targetHeight = this.measureDropdownHeightSP(content);

        // 初期状態をセット
        content.style.display = 'flex';
        content.style.visibility = 'visible';
        content.style.maxHeight = '0px';
        content.style.opacity = '0';
        content.style.overflow = 'hidden';

        // アニメーション開始
        this.animateDropdownShowSP(content, targetHeight);
    }

    /**
     * SP版サブプルダウンメニューの表示アニメーション（親要素を閉じない）
     * @param {HTMLElement} content - サブプルダウンコンテンツ要素
     */
    showSubDropdownSP(content) {
        // 親要素を特定
        const parentContent = this.findParentDropdown(content);

        // 他のサブプルダウンを閉じる（親要素は除く）
        this.hideOtherSubDropdowns(content, parentContent);

        // 既に進行中のアニメーションをキャンセル
        this.cancelAnimation(content);

        // 親要素のmax-heightを一時的に解除（サブプルダウンが表示できるように）
        if (parentContent && parentContent.classList.contains('is-show')) {
            parentContent.style.maxHeight = 'none';
        }

        // 実際の高さを測定
        const targetHeight = this.measureDropdownHeightSP(content);

        // 初期状態をセット
        content.style.display = 'flex';
        content.style.visibility = 'visible';
        content.style.maxHeight = '0px';
        content.style.opacity = '0';
        content.style.overflow = 'hidden';

        // アニメーション開始
        this.animateDropdownShowSP(content, targetHeight);
    }

    /**
     * SP版プルダウンメニューの非表示アニメーション
     * @param {HTMLElement} content - プルダウンコンテンツ要素
     */
    hideDropdownSP(content) {
        // 既に進行中のアニメーションをキャンセル
        this.cancelAnimation(content);

        // アニメーション開始
        this.animateDropdownHideSP(content);
    }

    /**
     * SP版プルダウンコンテンツの高さを測定
     * @param {HTMLElement} content - プルダウンコンテンツ要素
     * @returns {number} - 実際の高さ（px）
     */
    measureDropdownHeightSP(content) {
        // 元のスタイルを保存
        const originalStyles = {
            display: content.style.display,
            visibility: content.style.visibility,
            maxHeight: content.style.maxHeight,
            position: content.style.position,
            top: content.style.top,
            left: content.style.left
        };

        // 測定用の一時的なスタイル（画面外で表示）
        content.style.display = 'flex';
        content.style.visibility = 'hidden';
        content.style.maxHeight = 'none';
        content.style.position = 'absolute';
        content.style.top = '-9999px';
        content.style.left = '-9999px';

        // 実際の高さを取得
        const height = content.offsetHeight;

        // スタイルを元に戻す
        Object.assign(content.style, originalStyles);

        return height;
    }

    /**
     * SP版プルダウン表示アニメーション実行
     * @param {HTMLElement} content - プルダウンコンテンツ要素
     * @param {number} targetHeight - 目標の高さ
     */
    animateDropdownShowSP(content, targetHeight) {
        const duration = 350;
        const startTime = performance.now();

        const animate = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);

            // イージング関数（ease-out）
            const easeOut = 1 - Math.pow(1 - progress, 3);

            // プロパティの計算
            const currentHeight = targetHeight * easeOut;
            const currentOpacity = easeOut;

            // スタイルの適用
            content.style.maxHeight = `${currentHeight}px`;
            content.style.opacity = currentOpacity;

            if (progress < 1) {
                const animationId = requestAnimationFrame(animate);
                this.activeAnimations.set(content, animationId);
            } else {
                // アニメーション完了
                content.style.maxHeight = 'none'; // SP版では固定高さを削除
                content.style.opacity = '1';
                content.classList.add('is-show');
                this.activeAnimations.delete(content);
            }
        };

        const animationId = requestAnimationFrame(animate);
        this.activeAnimations.set(content, animationId);
    }

    /**
     * SP版プルダウン非表示アニメーション実行
     * @param {HTMLElement} content - プルダウンコンテンツ要素
     */
    animateDropdownHideSP(content) {
        const duration = 350;
        const startTime = performance.now();
        const startHeight = content.offsetHeight;

        const animate = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);

            // イージング関数（ease-out）
            const easeOut = 1 - Math.pow(1 - progress, 3);

            // プロパティの計算
            const currentHeight = startHeight * (1 - easeOut);
            const currentOpacity = 1 - easeOut;

            // スタイルの適用
            content.style.maxHeight = `${currentHeight}px`;
            content.style.opacity = currentOpacity;

            if (progress < 1) {
                const animationId = requestAnimationFrame(animate);
                this.activeAnimations.set(content, animationId);
            } else {
                // アニメーション完了
                this.completeHideAnimationSP(content);
            }
        };

        const animationId = requestAnimationFrame(animate);
        this.activeAnimations.set(content, animationId);
    }

    /**
     * SP版非表示アニメーション完了処理
     * @param {HTMLElement} content - プルダウンコンテンツ要素
     */
    completeHideAnimationSP(content) {
        content.style.display = 'none';
        content.style.visibility = 'hidden';
        content.style.maxHeight = '0px'; // 非表示時は0に戻す
        content.style.opacity = '0';
        content.classList.remove('is-show');

        // 対応するgnavItemからis-activeクラスを削除（SP・タブレットのみ）
        if (window.innerWidth <= this.breakpoint) {
            const gnavItem = this.findGnavItemByContent(content);
            if (gnavItem) {
                gnavItem.classList.remove('is-active');
            }
        }

        this.activeAnimations.delete(content);
    }
}
